/**
 * Composes the shell: capability check, boot, canvas, and the overlays for each phase.
 *
 * The phase in the store decides what is on screen. Everything per-frame happens inside
 * the canvas and never re-renders this component.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { GameCanvas } from './engine/GameCanvas';
import { LevelGeometry } from './engine/LevelGeometry';
import { LevelLighting } from './engine/LevelLighting';
import { PerfSampler } from './engine/PerfReadout';
import { PhysicsWorld } from './engine/PhysicsWorld';
import { Player } from './engine/Player';
import { arrivalSpawn, buildGeometry } from './game/profile/buildGeometry';
import { crossEntry, crossExit, levelById } from './game/progression';
import { loadActiveProfile } from './game/profile/loadProfile';
import { useWakeLock } from './hooks/useWakeLock';
import { CarryController } from './engine/CarryController';
import { PocketController } from './engine/PocketController';
import type { PlacedObject } from './game/profile/buildGeometry';
import { DragController } from './engine/DragController';
import { LostAndFound } from './engine/LostAndFound';
import { CarryableObject } from './engine/CarryableObject';
import { MazeTables } from './engine/MazeTables';
import { Door } from './engine/Door';
import { ExteriorWorld } from './engine/ExteriorWorld';
import { DropZone } from './engine/DropZone';
import { PortalGate } from './engine/PortalGate';
import { HouseBackdrop } from './engine/HouseBackdrop';
import { InteractionSystem } from './engine/InteractionSystem';
import { Moon } from './engine/Moon';
import {
  ContextActionButton,
  JumpButton,
  PauseButton,
  ThrowButton,
  StowButton,
  TouchControls,
} from './input/TouchControls';
import { InteractionPrompt } from './ui/InteractionPrompt';
import { ObjectiveHud } from './ui/ObjectiveHud';
import { PartTallyHud } from './ui/PartTallyHud';
import { PocketHud } from './ui/PocketHud';
import { useActiveScheme } from './input/useActiveScheme';
import { useInputProducers } from './input/useInputProducers';
import { useGameStore } from './store/gameStore';
import { Crosshair } from './ui/Crosshair';
import { LoadingScreen } from './ui/LoadingScreen';
import { OrientationGate } from './ui/OrientationGate';
import { CompletionOverlay } from './ui/CompletionOverlay';
import { PauseOverlay } from './ui/PauseOverlay';
import { UnsupportedBrowser } from './ui/UnsupportedBrowser';
import { UpdateNotice } from './ui/UpdateNotice';
import { isWebGLAvailable } from './utils/webgl';
import type { Ladder } from './game/maze/placeLadders';

/** Hoisted so an indoor level hands `Player` the same empty array on every render. */
const NO_LADDERS: readonly Ladder[] = [];

/**
 * How long the screen stays covered before one level is torn down and the next is built.
 * Long enough to read as a passage rather than a stutter, and comfortably longer than the
 * cover's own fade-in (180ms, in `styles.css`) — the screen has to be black *before* the
 * swap frame, not black as it happens, or the cover hides nothing.
 */
/** A level with nothing built yet has no objects; the touch HUD still renders. */
const NO_OBJECTS: readonly PlacedObject[] = [];

const FADE_MS = 450;

export function App() {
  const phase = useGameStore((store) => store.phase);
  const dispatch = useGameStore((store) => store.dispatch);
  const profile = useGameStore((store) => store.profile);
  const currentLevelId = useGameStore((store) => store.currentLevelId);
  const arriveAt = useGameStore((store) => store.arriveAt);

  // One level is in the world at a time. The rest of the profile is content, not memory:
  // crossing a gate rebuilds this, React unmounts the subtree below and the level that was
  // there — geometry, colliders, textures — goes with it.
  const level = profile && currentLevelId ? levelById(profile, currentLevelId) : null;
  const built = useMemo(
    () => (profile && level ? buildGeometry(level, profile.palette) : null),
    [profile, level],
  );

  // Whether another level's exit leads here, which decides two things together: whether
  // the entry gate is mounted at all, and whether arriving at the entry end is a crossing
  // (through that gate, and past it) or the start of a run.
  const entryLeadsHere = profile && level ? crossEntry(profile, level.id) !== null : false;
  const spawn = built ? arrivalSpawn(built, arriveAt, entryLeadsHere) : undefined;

  const surfaceRef = useRef<HTMLDivElement>(null);
  const perfRef = useRef<HTMLDivElement>(null);
  const scheme = useActiveScheme();

  // Checked once, before the canvas is ever mounted — mounting it without WebGL gives a
  // black rectangle and no explanation.
  const [webglAvailable] = useState(isWebGLAvailable);

  useInputProducers(surfaceRef);
  useWakeLock(phase === 'playing');

  // The swap itself, deliberately out here rather than in the gate's collision callback:
  // by the time this runs Rapier has finished with the frame that triggered it, the world
  // is paused, and the fade is opaque. See design D5.
  useEffect(() => {
    if (phase !== 'crossing') return;
    const timer = window.setTimeout(() => useGameStore.getState().arrive(), FADE_MS);
    return () => window.clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    if (!webglAvailable) {
      dispatch('UNSUPPORTED');
      return;
    }

    // Settings are rehydrated and the profile loaded before the first frame, so look
    // sensitivity is never wrong and the world never renders half-built. A profile
    // failure here should be impossible — the build refuses invalid profiles — so it
    // is logged and the game stays on the loading screen rather than guessing.
    void Promise.all([useGameStore.getState().hydrate(), loadActiveProfile()])
      .then(([, profile]) => {
        useGameStore.getState().setProfile(profile);
        dispatch('LOADED');
      })
      .catch((error: unknown) => {
        console.error('Failed to load the game profile', error);
      });
  }, [dispatch, webglAvailable]);

  if (phase === 'unsupported') return <UnsupportedBrowser />;

  const showTouchControls = phase === 'playing' && scheme === 'touch';

  return (
    <div className="game-surface" ref={surfaceRef}>
      {/* Three things the canvas itself has to know about the level. Outdoors the scene fog
          runs much further, because out there the fog is what makes distance read rather
          than what hides the far end of a room. And `ready` says the world below has
          actually been built — the canvas preloads on that flag, and preloading an empty
          scene would be a wasted traversal and a stutter left in place. Null until the
          profile has loaded, which is the loading screen — nothing is drawn through it
          yet. And `sceneKey` says *which* level, so that crossing a portal redraws the
          frozen shadow map: nothing else about the canvas changes between two mazes, and
          without this the maze the player arrives in is lit by the one they left. */}
      <GameCanvas
        outdoors={built?.grounds != null}
        ready={built != null}
        sceneKey={level?.id ?? ''}
      >
        <PhysicsWorld paused={phase !== 'playing'}>
          {built && profile && level && spawn && (
            // Keyed by the level: React tears the whole subtree down and builds the next
            // one, rather than diffing one maze into another.
            <group key={level.id}>
              <ExteriorWorld built={built} seed={level.id} />
              <LevelLighting built={built} level={level} />
              <LevelGeometry built={built} level={level} />
              {built.doorways.map((doorway) => (
                <Door key={doorway.id} doorway={doorway} color={profile.palette.door} />
              ))}
              {built.objects.map((object) => (
                <CarryableObject key={object.id} object={object} boxColor={profile.palette.box} />
              ))}
              {built.zones.map((zone) => (
                <DropZone key={zone.objectiveId} zone={zone} color={profile.palette.zone} />
              ))}
              {/* Outdoors only, and empty-safe rather than branched: a house has no
                  grounds, and a maze has no doors, objects or drop zones — every map
                  above it simply runs over an empty array. The hedges and the moat
                  themselves are drawn inside `LevelGeometry`, with the rest of the level's
                  geometry and its one shared rigid body. */}
              {/* Their own bodies, so they are not baked into the level's static collider:
                  a dragged table has to take its collider with it. */}
              {built.grounds && <MazeTables tables={built.grounds.tables} />}
              {built.grounds && <HouseBackdrop built={built} grounds={built.grounds} />}
              {built.grounds && <Moon built={built} />}
              {/* The way on, and the way back. The exit gate always stands — it is how a
                  maze is finished, whether that leads to another level or to the end of the
                  run. The entry gate stands only where some level's exit leads here, so the
                  first maze has an opening behind the player and nothing in it.
                  Which of the two this exit is also decides how it looks: the gate that ends
                  the run is a door with an EXIT sign, the ones with a maze after them keep
                  the glow, so the two never read alike. The gate itself does not ask the
                  store — that decision is made here. */}
              {built.grounds && (
                <PortalGate
                  gate={built.grounds.exitGate}
                  role="exit"
                  levelId={level.id}
                  objectiveId={built.grounds.exitObjectiveId}
                  inside={built.grounds.exitArrival.position}
                  finish={crossExit(profile, level.id) === 'complete'}
                />
              )}
              {built.grounds && entryLeadsHere && (
                <PortalGate
                  gate={built.grounds.entryGate}
                  role="entry"
                  levelId={level.id}
                  inside={built.grounds.entryArrival.position}
                />
              )}
              {/* Keyed by the end as well as the level: walking back into a maze you have
                  already crossed has to put the body at the other end of it and reset the
                  look, which is a fresh mount, not a moved prop. */}
              <Player
                key={`${level.id}:${arriveAt}`}
                spawn={spawn}
                ladders={built.grounds?.ladders ?? NO_LADDERS}
                bounds={built.grounds?.bounds}
              />
              <InteractionSystem />
              <CarryController />
              <PocketController objects={built.objects} />
              <DragController />
              <LostAndFound lawn={built.grounds?.lawn} />
            </group>
          )}
        </PhysicsWorld>
        {import.meta.env.DEV && <PerfSampler target={perfRef} />}
      </GameCanvas>

      {phase === 'loading' && <LoadingScreen />}
      {phase === 'playing' && <Crosshair />}
      {phase === 'playing' && <InteractionPrompt showBinding={scheme === 'desktop'} />}
      {phase === 'playing' && (
        <div className="hud-corner">
          <ObjectiveHud />
          <PartTallyHud objects={built?.objects ?? NO_OBJECTS} />
        </div>
      )}
      {phase === 'playing' && <PocketHud />}
      {/* Only during play: while paused, the restart action in the overlay says it. */}
      {phase === 'playing' && <UpdateNotice />}
      {showTouchControls && (
        <>
          <TouchControls />
          <ContextActionButton />
          <JumpButton />
          <ThrowButton />
          <StowButton objects={built?.objects ?? NO_OBJECTS} />
          {/* The only way to reach the pause overlay without a keyboard: iOS Safari has
              no pointer lock to lose, and the orientation gate resumes on its own. */}
          <PauseButton />
        </>
      )}
      {phase === 'paused' && <PauseOverlay onResume={() => dispatch('RESUME')} />}
      {phase === 'complete' && <CompletionOverlay />}
      {/* The crossing: a cover over the canvas rather than a post-processing pass, because
          the frame it hides is the most expensive one in the game and the GPU has enough to
          do. Kept mounted so the CSS transition has something to animate, and hidden from
          assistive tech — there is nothing here to read. */}
      <div className={`crossing-fade${phase === 'crossing' ? ' crossing-fade--on' : ''}`} aria-hidden />

      <OrientationGate />

      {import.meta.env.DEV && <div ref={perfRef} className="perf-readout" />}
    </div>
  );
}
